import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Rating } from '@mui/material'
import { toast } from 'react-toastify'
import { Button, TextAreaField } from 'src/components/ui'
import { useCreateReviewMutation } from 'src/redux/api'

interface ILeaveReviewForm {
  propertyId: string
  revieweeId: string
  onDone?: () => void
}

// Ratings & reviews (roadmap 41 H1). A compact star + comment form. The server
// enforces the interaction gate, so a rejection surfaces as a toast rather than
// being pre-hidden. Reused for buyer→owner and owner→buyer reviews.
const LeaveReviewForm: FC<ILeaveReviewForm> = ({ propertyId, revieweeId, onDone }) => {
  const { t } = useTranslation()
  const [createReview, { isLoading }] = useCreateReviewMutation()
  const [rating, setRating] = useState<number | null>(0)
  const [comment, setComment] = useState('')
  const [anonymous, setAnonymous] = useState(false)

  const submit = async () => {
    if (!rating) return
    try {
      await createReview({
        userId: revieweeId,
        propertyId,
        rating,
        comment: comment.trim() || undefined,
        anonymous,
      }).unwrap()
      toast.success(t('review.submitted', 'Thanks for your review'))
      setRating(0)
      setComment('')
      onDone?.()
    } catch {
      // toastMiddleware surfaces the backend reason (e.g. no interaction yet)
    }
  }

  return (
    <div className="flex flex-col gap-2 w-full bg-surface rounded-lg p-3">
      <span className="text-sm font-semibold text-ink">{t('review.yourRating')}</span>
      <Rating value={rating} onChange={(_, value) => setRating(value)} size="small" />
      <TextAreaField
        className="!border !border-ink/[0.18] !h-16"
        name="reviewComment"
        placeholder={t('review.commentPlaceholder')}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
        <input
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
          className="w-4 h-4 accent-brand"
        />
        {t('review.anonymously')}
      </label>
      <Button type="button" disabled={isLoading || !rating} className="!h-9 !py-1" onClick={submit}>
        {t('review.submit')}
      </Button>
    </div>
  )
}

export default LeaveReviewForm
